import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useGenerationAssets } from '../components/generation/state/assetsContext';
import { useUploadTemporaryMaterial } from '../features/materials/hooks';
import { useIsMobile } from '../hooks/useIsMobile';
import { Button } from '../components/common/Button';
import SimpleMaterialsList, { type SimpleMaterialsListRef } from '../components/panels/SimpleMaterialsList';
import { UploadMaterialModal } from '../components/materials/UploadMaterialModal';
import { brandConfig } from '../config/brands';
import styles from './MaterialsBrowsePage.module.css';
import '../styles/buttons.css';

interface MaterialsBrowsePageProps {
  defaultFolder?: 'shared' | 'personal';
}

function resolveInitialTab(defaultFolder?: 'shared' | 'personal'): 'cloud' | 'temporary' {
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (path.startsWith('/materials/personal')) return 'temporary';
    if (path.startsWith('/materials/shared')) return 'cloud';
  }
  return defaultFolder === 'personal' ? 'temporary' : 'cloud';
}

export default function MaterialsBrowsePage({ defaultFolder }: MaterialsBrowsePageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedAssets, addAsset, removeAsset, shouldOpenPersonalFolder, clearOpenPersonalFolderRequest } = useGenerationAssets();

  // SimpleMaterialsList の TabType に合わせて 'storyboard' も型上は許容する。
  // /materials/* ページでは storyboardImageUrl が立たないため実際は素材タブのみ。
  const [activeTab, setActiveTab] = useState<'cloud' | 'temporary' | 'storyboard'>(() => resolveInitialTab(defaultFolder));
  const [bulkEditMode, setBulkEditMode] = useState<'none' | 'delete' | 'tags' | 'move' | 'product-link'>('none');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const materialsListRef = useRef<SimpleMaterialsListRef>(null);
  const uploadTempMutation = useUploadTemporaryMaterial();
  const isCameraUploading = uploadTempMutation.isPending;
  const isMobile = useIsMobile();

  useEffect(() => {
    queryClient.refetchQueries({ queryKey: [activeTab === 'cloud' ? 'cloud-materials' : 'temporary-materials'] });
  }, [activeTab, queryClient]);

  useEffect(() => {
    const syncTabFromUrl = () => {
      const path = window.location.pathname;
      if (path.startsWith('/materials/personal')) {
        setActiveTab((prev) => (prev === 'temporary' ? prev : 'temporary'));
      } else if (path.startsWith('/materials/shared')) {
        setActiveTab((prev) => (prev === 'cloud' ? prev : 'cloud'));
      }
    };
    window.addEventListener('popstate', syncTabFromUrl);
    return () => window.removeEventListener('popstate', syncTabFromUrl);
  }, []);

  useEffect(() => {
    if (shouldOpenPersonalFolder) {
      setActiveTab('temporary');
      clearOpenPersonalFolderRequest();
      setTimeout(() => {
        materialsListRef.current?.scrollToTop();
      }, 300);
    }
  }, [shouldOpenPersonalFolder, clearOpenPersonalFolderRequest]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  const handleCameraCapture = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadTempMutation.mutateAsync({ file, source_type: 'camera' });
    if (result?.id) {
      const thumbnailUrl = result.thumbnail_url || result.preview_url || result.storage_path || '';
      addAsset({
        id: result.id,
        thumbnailUrl,
        type: 'temporary',
        storageBucket: result.storage_bucket ?? undefined,
        storagePath: result.storage_path ?? undefined,
        contentType: result.content_type,
        width: result.width,
        height: result.height,
      });
    }
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  }, [uploadTempMutation, addAsset]);

  const pageTitle = `${brandConfig.labels.material}一覧`;

  return (
    <div className={styles.page}>
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/picture_stack_landscape_3.svg" alt="" className="page-title-icon page-title-icon--mobile-hidden" />
            {pageTitle}
          </h1>
        </div>
        <div className="page-header-actions">
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => navigate('/products')}
          >
            <img src="/database_2.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px' }} />
            {brandConfig.labels.productShort}データ取込
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => setUploadModalOpen(true)}
          >
            <img src="/export_file.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px' }} />
            素材アップロード
          </Button>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleCameraCapture}
          />
          {isMobile && (
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={() => cameraInputRef.current?.click()}
              disabled={isCameraUploading}
              loading={isCameraUploading}
              aria-label="カメラで撮影"
            >
              <img src="/camera_light.svg" alt="" style={{ width: '16px', height: '16px' }} />
            </Button>
          )}
          <div className={styles.menuWrapper} ref={menuRef}>
            <Button
              type="button"
              variant="muted"
              size="md"
              onClick={() => {
                if (bulkEditMode !== 'none') {
                  setBulkEditMode('none');
                  return;
                }
                setMenuOpen(!menuOpen);
              }}
            >
              {bulkEditMode !== 'none' ? '一括編集をキャンセル' : '一括編集'}
              {bulkEditMode === 'none' && (
                <span className={styles.caret} aria-hidden="true">▼</span>
              )}
            </Button>
            {bulkEditMode === 'none' && menuOpen && (
              <div className={styles.dropdown}>
                <div className={styles.dropdownLabel}>一括編集</div>
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => { setMenuOpen(false); setBulkEditMode('delete'); }}
                >
                  素材の削除
                </button>
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => { setMenuOpen(false); setBulkEditMode('tags'); }}
                >
                  タグの追加・削除
                </button>
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => { setMenuOpen(false); setBulkEditMode('move'); }}
                >
                  {activeTab === 'temporary' ? '共有フォルダに移動' : '個人フォルダに移動'}
                </button>
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => { setMenuOpen(false); setBulkEditMode('product-link'); }}
                >
                  {brandConfig.labels.productShort}の関連付け
                </button>
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="muted"
            size="md"
            onClick={() => navigate('/trash')}
          >
            ごみ箱
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <SimpleMaterialsList
          ref={materialsListRef}
          mode="manage"
          paginated
          bulkEditMode={bulkEditMode}
          onBulkEditComplete={() => setBulkEditMode('none')}
          selectedAssets={selectedAssets}
          onAddAsset={addAsset}
          onRemoveAsset={removeAsset}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      <UploadMaterialModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        defaultDestination={activeTab === 'cloud' ? 'cloud' : 'temporary'}
      />
    </div>
  );
}
